import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Login from './Login';
import Signup from './Signup';
import TagsManager from './TagsManager';
import LinesManager from './LinesManager';
import PracticeSession from './PracticeSession';
import PracticeGames from './PracticeGames';
import DrillsGames from './DrillsGames';
import DrillsSession from './DrillsSession';
import Dashboard from './Dashboard';
import { authAPI } from '../services/api';
import './Home.css';

const Home = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isLogin, setIsLogin] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [activeSection, setActiveSection] = useState('dashboard');
  const [activeContentTab, setActiveContentTab] = useState('tags');
  const [currentThreadId, setCurrentThreadId] = useState(null);
  const [currentTagId, setCurrentTagId] = useState(null);
  const [currentDrillTagId, setCurrentDrillTagId] = useState(null);
  const [isMobileNavOpen, setIsMobileNavOpen] = useState(false);

  // Check if user is already authenticated on component mount
  useEffect(() => {
    const checkAuth = () => {
      if (authAPI.isAuthenticated()) {
        const currentUser = authAPI.getCurrentUser();
        if (currentUser) {
          setUser(currentUser);
          setIsAuthenticated(true);
          // loadThreads(); // Load threads when authenticated
        }
      }
      setIsLoading(false);
    };

    checkAuth();
  }, []);

  // Handle URL changes to update active section and current thread
  useEffect(() => {
    if (isAuthenticated) {
      const path = location.pathname;
      
      if (path === '/') {
        setActiveSection('dashboard');
        setCurrentThreadId(null);
        setCurrentTagId(null);
      } else if (path === '/practice') {
        setActiveSection('practice');
        setCurrentThreadId(null);
        setCurrentTagId(null);
        // loadThreads(); // Load threads when navigating to practice section
      } else if (path.startsWith('/practice/recall/')) {
        setActiveSection('practice');
        const tagId = path.split('/practice/recall/')[1];
        setCurrentTagId(tagId);
        setCurrentThreadId(null);
      } else if (path.startsWith('/practice/')) {
        setActiveSection('practice');
        const threadId = path.split('/practice/')[1];
        setCurrentThreadId(threadId);
        setCurrentTagId(null);
        // loadThreads(); // Load threads when navigating to specific practice session
      } else if (path === '/content') {
        setActiveSection('content');
        setCurrentThreadId(null);
        setCurrentTagId(null);
        setCurrentDrillTagId(null);
      } else if (path === '/drills') {
        setActiveSection('drills');
        setCurrentDrillTagId(null);
        setCurrentThreadId(null);
        setCurrentTagId(null);
      } else if (path.startsWith('/drills/recall/')) {
        setActiveSection('drills');
        const tagId = path.split('/drills/recall/')[1];
        setCurrentDrillTagId(tagId);
        setCurrentThreadId(null);
        setCurrentTagId(null);
      } else if (path.startsWith('/drills/cue/')) {
        setActiveSection('drills');
        const tagId = path.split('/drills/cue/')[1];
        setCurrentDrillTagId(tagId);
        setCurrentThreadId(null);
        setCurrentTagId(null);
      }
    }
  }, [location.pathname, isAuthenticated]);

  useEffect(() => {
    setIsMobileNavOpen(false);
  }, [location.pathname]);

  // const loadThreads = async () => {
  //   try {
  //     setIsLoadingThreads(true);
  //     const response = await threadsAPI.getAll();
  //     // Handle both paginated (response.results) and non-paginated (direct array) responses
  //     const threadsList = response.results || response || [];
  //     setThreads(threadsList);
  //   } catch (error) {
  //     console.error('Error loading threads:', error);
  //   } finally {
  //     setIsLoadingThreads(false);
  //   }
  // };

  const handleLoginSuccess = (userData) => {
    setUser(userData);
    setIsAuthenticated(true);
    // loadThreads(); // Load threads after successful login
  };

  const handleSignupSuccess = (userData) => {
    setUser(userData);
    setIsAuthenticated(true);
  };

  const handleLogout = async () => {
    await authAPI.logout();
    setUser(null);
    setIsAuthenticated(false);
  };

  const switchToSignup = () => {
    setIsLogin(false);
  };

  const switchToLogin = () => {
    setIsLogin(true);
  };

  const navigationItems = [
    {
      key: 'dashboard',
      path: '/',
      icon: 'DB',
      label: 'Dashboard'
    },
    {
      key: 'content',
      path: '/content',
      icon: 'CT',
      label: 'Content'
    },
    {
      key: 'drills',
      path: '/drills',
      icon: 'DR',
      label: 'Drills'
    }
  ];

  const activeNavItem = navigationItems.find((item) => item.key === activeSection) || navigationItems[0];
  const landingFeatures = [
    {
      id: '01',
      title: 'Tag libraries',
      description: 'Keep lines grouped by scenario so recall practice stays specific.'
    },
    {
      id: '02',
      title: 'Timed drills',
      description: 'Run focused sessions instead of vague free-form rehearsal.'
    },
    {
      id: '03',
      title: 'Score review',
      description: 'See weak spots, alternatives, and response quality after every round.'
    }
  ];

  const handleNavigate = (path) => {
    navigate(path);
    setIsMobileNavOpen(false);
  };

  // Show loading state while checking authentication
  if (isLoading) {
    return (
      <div className="home-container">
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  // Show dashboard if authenticated
  if (isAuthenticated && user) {
    return (
      <div className="home-container app-shell">
        <div className="app-frame">
          <header className="app-header">
            <div className="app-header-title">
              <h1>{activeNavItem.label}</h1>
            </div>
            <div className="app-header-actions">
              <button
                type="button"
                className={`mobile-nav-toggle ${isMobileNavOpen ? 'open' : ''}`}
                onClick={() => setIsMobileNavOpen((open) => !open)}
                aria-label="Toggle navigation menu"
                aria-expanded={isMobileNavOpen}
                aria-controls="mobile-nav-menu"
              >
                <span className="mobile-nav-toggle-icon" aria-hidden="true">
                  <span></span>
                  <span></span>
                  <span></span>
                </span>
                <span className="mobile-nav-toggle-label">Menu</span>
              </button>
            </div>
          </header>

          <div className={`mobile-nav-container ${isMobileNavOpen ? 'open' : ''}`}>
            <nav id="mobile-nav-menu" className="mobile-nav-menu" aria-label="Mobile navigation">
              {navigationItems.map((item) => (
                <button
                  key={item.key}
                  className={`mobile-nav-item ${activeSection === item.key ? 'active' : ''}`}
                  onClick={() => handleNavigate(item.path)}
                >
                  <span className="mobile-nav-icon">{item.icon}</span>
                  <span className="mobile-nav-label">{item.label}</span>
                </button>
              ))}
              <div className="mobile-nav-divider" aria-hidden="true"></div>
              <button
                type="button"
                className="mobile-nav-item mobile-logout-item"
                onClick={handleLogout}
              >
                <span className="mobile-nav-icon">↩</span>
                <span className="mobile-nav-label">Logout</span>
              </button>
            </nav>
          </div>
          
          <div className="app-layout">
            {/* Left Navigation */}
            <div className="left-nav">
              <div className="nav-brand">
                <h2>Crouton</h2>
              </div>

              <div className="nav-section">
                <h3>Navigation</h3>
                <nav className="nav-menu">
                  {navigationItems.map((item) => (
                    <button
                      type="button"
                      key={item.key}
                      className={`nav-item ${activeSection === item.key ? 'active' : ''}`}
                      onClick={() => handleNavigate(item.path)}
                    >
                      <span className="nav-icon">{item.icon}</span>
                      <span className="nav-label">{item.label}</span>
                    </button>
                  ))}
                </nav>
              </div>

              <div className="nav-section">
                <h3>Account</h3>
                <nav className="nav-menu">
                  <button
                    type="button"
                    className="nav-item nav-item-logout"
                    onClick={handleLogout}
                  >
                    <span className="nav-icon">↩</span>
                    <span className="nav-label">Logout</span>
                  </button>
                </nav>
              </div>
              
              {/* Session Threads (only visible in practice section) */}
              {/* {activeSection === 'practice' && (
                <div className="nav-section">
                  <h3>Session History</h3>
                  <div className="session-threads">
                    {isLoadingThreads ? (
                      <div className="loading-threads">Loading sessions...</div>
                    ) : threads.length === 0 ? (
                      <div className="no-threads">No practice sessions yet</div>
                    ) : (
                      threads.map(thread => (
                        <div 
                          key={thread.thread_id} 
                          className={`thread-item ${currentThreadId === thread.thread_id ? 'selected' : ''}`}
                          onClick={() => navigate(`/practice/${thread.thread_id}`)}
                        >
                          <span className="thread-title">{thread.thread_name}</span>
                          <span className="thread-date">
                            {new Date(thread.last_activity).toLocaleDateString()}
                          </span>
                          <span className="thread-status">{thread.status}</span>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              )} */}
            </div>
            
            {/* Main Content Area */}
            <div className="main-content">
              {activeSection === 'dashboard' && (
                <div className="dashboard-section">
                  <Dashboard />
                </div>
              )}
              
              {activeSection === 'content' && (
                <div className="content-section">
                  <div className="content-tabs">
                    <div className="tab-buttons">
                      <button 
                        className={`tab-button ${activeContentTab === 'tags' ? 'active' : ''}`}
                        onClick={() => setActiveContentTab('tags')}
                      >
                        Tags
                      </button>
                      <button 
                        className={`tab-button ${activeContentTab === 'lines' ? 'active' : ''}`}
                        onClick={() => setActiveContentTab('lines')}
                      >
                        Lines
                      </button>
                    </div>
                  </div>
                  <div className="content-area">
                    {activeContentTab === 'tags' && <TagsManager />}
                    {activeContentTab === 'lines' && <LinesManager />}
                  </div>
                </div>
              )}
              
              {activeSection === 'practice' && (
                <div className="practice-section">
                  {currentThreadId || currentTagId ? (
                    <PracticeSession 
                      threadId={currentThreadId} 
                      tagId={currentTagId}
                      onThreadCreated={() => {}} 
                    />
                  ) : (
                    <PracticeGames />
                  )}
                </div>
              )}
              
              {activeSection === 'drills' && (
                <div className="drills-section">
                  {currentDrillTagId ? (
                    <DrillsSession 
                      tagId={currentDrillTagId} 
                      gameType={location.pathname.startsWith('/drills/cue/') ? 'cue_game' : 'drill'}
                      locationState={location.state}
                    />
                  ) : (
                    <DrillsGames />
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Show authentication forms if not authenticated
  return (
    <div className="home-container landing-page">
      <div className="landing-shell">
        <div className="hero-section">
          <span className="hero-eyebrow">Conversation training cockpit</span>
          <div className="hero-content">
            <h1 className="hero-title">Sharpen recall for the lines you actually use.</h1>
            <p className="hero-subtitle">
              Crouton keeps your lines, drills, and session review in one focused workspace.
            </p>
          </div>
          <div className="hero-features">
            {landingFeatures.map((feature) => (
              <div key={feature.id} className="feature">
                <span className="feature-icon">{feature.id}</span>
                <span className="feature-title">{feature.title}</span>
                <span className="feature-copy">{feature.description}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="auth-section">
          <div className="auth-section-copy">
            <span className="auth-section-eyebrow">
              {isLogin ? 'Welcome back' : 'New workspace'}
            </span>
            <p>
              {isLogin
                ? 'Pick up your training where you left off.'
                : 'Create an account and start building a cleaner practice loop.'}
            </p>
          </div>
          {isLogin ? (
            <Login 
              onLoginSuccess={handleLoginSuccess}
              onSwitchToSignup={switchToSignup}
            />
          ) : (
            <Signup 
              onSignupSuccess={handleSignupSuccess}
              onSwitchToLogin={switchToLogin}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default Home;
